import { CSSObject } from '@emotion/react'
import { Fragment, useState, DragEvent } from 'react'
import {
  resetPostEditorDragListener,
  unsetPostEditorDragListener,
} from '@opallabs/brown-dwarf'
import { AssetReference } from '@opallabs/cassini'
import {
  Asset,
  kuiperClassNames,
  WithTooltip,
  renderIcon,
} from '@opallabs/kuiper'
import { getNestedProperty } from '@opallabs/lambda'
import { DraggableAsset } from './DraggableAsset'
import { styles as draggableStyles } from './DraggableAsset/styles'
import useGalleryPositions from './hooks/useGalleryPositions'
import type { UseGalleryPositionsReturn } from './hooks/useGalleryPositions'
import type { GalleryPositionGroups } from './positions'
import { RemainingAssetsPreview } from './RemainingAssetsPreview'
import { styles } from './styles'

interface Props {
  assets: AssetReference[]
  isPresenting: boolean
  display: string
  galleryContainerStyles: CSSObject
  hasAssetOptions: boolean
  maxHeight: number
  maxWidth: number
  onAssetChange: (orderedAssets: AssetReference[]) => void
  onExpandAsset: (assetId: number) => void
  onRemoveAsset: (assetId: number) => void
  positions: GalleryPositionGroups[][]
  readOnly: boolean
  maxAssets: number
  updateCaption: (value: string, assetId: number) => void
}

type DragStateProps = {
  dragging: boolean
  dragZone: number | null
}

export const DragAndDropAssetGallery: React.FC<Props> = ({
  assets = [],
  isPresenting,
  display = '',
  galleryContainerStyles,
  hasAssetOptions,
  maxHeight,
  maxWidth,
  onAssetChange,
  onExpandAsset,
  onRemoveAsset,
  positions,
  maxAssets,
  readOnly,
  updateCaption,
}) => {
  const [isTooltipActive, setIsTooltipActive] = useState<boolean>(false)
  const [draggedAsset, setDraggedAsset] = useState<AssetReference | null>(null)
  const [dragState, setDragState] = useState<DragStateProps>({
    dragging: false,
    dragZone: null,
  })
  let assetLoopIndex = -1

  const {
    orderedAssets,
    setOrderedAssets,
    galleryPositions,
  }: UseGalleryPositionsReturn = useGalleryPositions(
    assets,
    maxAssets,
    positions
  )
  let areAssetsHorizontallyAligned = true
  if (display === 'facebook standard') {
    areAssetsHorizontallyAligned =
      galleryPositions[0].height === 1 ||
      (galleryPositions[0].direction === 'column' && orderedAssets.length > 4)
  }
  const remainingAssets = orderedAssets.slice(maxAssets)

  const updateAssetPositions = (zone: number) => {
    const newAssetArr = orderedAssets
    const assetIndex = orderedAssets.findIndex(
      (asset) => draggedAsset && asset.id === draggedAsset.id
    )
    newAssetArr.splice(assetIndex, 1)
    newAssetArr.splice(zone, 0, draggedAsset as AssetReference)
    setOrderedAssets(newAssetArr)
  }

  const onDragStart = (e: DragEvent, asset: AssetReference) => {
    unsetPostEditorDragListener()
    const currentDragZone = parseInt(
      getNestedProperty(e, 'target', 'dataset', 'zoneId') || '',
      10
    )
    e.dataTransfer.setData('text', '')
    e.dataTransfer.setDragImage(e.target as Element, 20, 20)
    e.dataTransfer.dropEffect = 'move'
    setDraggedAsset(asset)
    setDragState({ dragging: true, dragZone: currentDragZone })
  }

  const onDragEnter = (e: DragEvent) => {
    e.preventDefault()
    const currentDragZone = parseInt(
      getNestedProperty(e, 'target', 'dataset', 'zoneId') || '',
      10
    )
    updateAssetPositions(currentDragZone)
    setDragState({ dragging: true, dragZone: currentDragZone })
  }

  const onDragComplete = (e: DragEvent) => {
    const currentDragZone = parseInt(
      getNestedProperty(e, 'target', 'dataset', 'zoneId') || '',
      10
    )
    updateAssetPositions(currentDragZone)
    setDragState({ dragging: false, dragZone: null })
    setDraggedAsset(null)
    onAssetChange(orderedAssets)
    resetPostEditorDragListener()
  }

  const toggleTooltip = () => {
    const newTooltipValue = !isTooltipActive
    setIsTooltipActive(newTooltipValue)
  }

  return orderedAssets.length > 0 ? (
    <div
      css={styles.getGalleryContainer(
        galleryContainerStyles,
        areAssetsHorizontallyAligned
      )}
    >
      {galleryPositions.map((assetGroup, i) => (
        <div
          key={`gallery-group-${i}`}
          css={styles.getImgContainerStyling(assetGroup.direction)}
        >
          {Array(assetGroup.numAssets)
            .fill(0)
            .map(() => {
              assetLoopIndex += 1
              /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
              return (
                <Fragment key={`gallery-asset-${assetLoopIndex}`}>
                  {remainingAssets.length > 0 &&
                  assetLoopIndex === maxAssets - 1 ? (
                    <div
                      css={styles.getFinalImagePreviewWrapper(
                        maxHeight,
                        maxWidth,
                        assetGroup
                      )}
                    >
                      <div
                        css={styles.getFinalImagePreview(
                          maxHeight,
                          maxWidth,
                          assetGroup
                        )}
                      >
                        <span style={styles.remainingImageMarker}>
                          +{orderedAssets.length - maxAssets + 1}
                        </span>
                      </div>
                      {(!readOnly || isPresenting) && (
                        <div
                          className={kuiperClassNames(
                            `remaining-assets-view${
                              isTooltipActive ? '-active' : ''
                            }`
                          )}
                        >
                          <WithTooltip
                            disabled={isTooltipActive}
                            content="view remaining assets"
                            tooltipOptions={{ position: 'top' }}
                            className="remaining-assets"
                          >
                            <div
                              className={kuiperClassNames(
                                'remaining-assets-popover'
                              )}
                            >
                              <div
                                className={kuiperClassNames(
                                  'remaining-assets-popover-body'
                                )}
                                css={styles.getPopoverStyling(
                                  maxWidth,
                                  assetGroup,
                                  isTooltipActive
                                )}
                              >
                                <RemainingAssetsPreview
                                  assets={[
                                    orderedAssets[assetLoopIndex],
                                    ...remainingAssets,
                                  ]}
                                  dragState={dragState}
                                  maxAssets={maxAssets}
                                  onDragComplete={onDragComplete}
                                  onDragEnter={onDragEnter}
                                  onDragStart={onDragStart}
                                  readOnly={readOnly}
                                  updateCaption={updateCaption}
                                  isPresenting={isPresenting}
                                  onExit={toggleTooltip}
                                  onExpandAsset={onExpandAsset}
                                  onRemoveAsset={onRemoveAsset}
                                />
                              </div>
                              <div
                                className={kuiperClassNames(
                                  'remaining-assets-cog'
                                )}
                                onClick={toggleTooltip}
                              >
                                {renderIcon('cog-lg', {
                                  ariaLabel:
                                    'Remaining Assets Popover Icon Trigger',
                                })}
                              </div>
                            </div>
                          </WithTooltip>
                        </div>
                      )}
                      <Asset
                        imageFit="cover"
                        hasAssetOverlay={false}
                        hasAssetOptions={hasAssetOptions}
                        assetReference={orderedAssets[assetLoopIndex]}
                        placeholder={true}
                        removeable={!isPresenting}
                        updateCaption={updateCaption}
                        overrideStyles={{
                          assetBase: draggableStyles.getImgStyling(
                            maxHeight,
                            maxWidth,
                            assetGroup
                          ),
                        }}
                        onEmbiggen={() =>
                          onExpandAsset(orderedAssets[assetLoopIndex].id || 0)
                        }
                        onRemove={() =>
                          onRemoveAsset(orderedAssets[assetLoopIndex].id || 0)
                        }
                      />
                    </div>
                  ) : (
                    <DraggableAsset
                      zone={assetLoopIndex}
                      maxHeight={maxHeight}
                      maxWidth={maxWidth}
                      hasAssetOptions={hasAssetOptions}
                      asset={orderedAssets[assetLoopIndex]}
                      dragZone={dragState.dragZone}
                      isDragging={dragState.dragging}
                      isTooltipActive={isTooltipActive}
                      isPresenting={isPresenting}
                      onDragComplete={onDragComplete}
                      onDragEnter={onDragEnter}
                      onDragStart={onDragStart}
                      onExpandAsset={onExpandAsset}
                      onRemoveAsset={onRemoveAsset}
                      readOnly={readOnly}
                      style={assetGroup}
                      updateCaption={updateCaption}
                    />
                  )}
                </Fragment>
              )
              /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
            })}
        </div>
      ))}
    </div>
  ) : (
    <Fragment></Fragment>
  )
}
